import { Link } from 'react-router-dom';
import logo from '../images/logo.png';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, LOGO_BASE_URL } from '../apiConfig';
import { Mail, MapPin, Facebook, Instagram, Youtube, Twitter, Shield, Trophy, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import useBreakpoint from '../hooks/useBreakpoint';

function Footer() {
    const [socials, setSocials] = useState({});
    const [divisions, setDivisions] = useState([]);
    const [partners, setPartners] = useState([]);
    const [sponsor, setSponsor] = useState(null);
    const [settings, setSettings] = useState({});
    const { isMobile } = useBreakpoint();

    useEffect(() => {
        let isMounted = true;
        
        // Fetch All Footer Data
        const fetchData = async () => {
            try {
                const [socialsRes, divisionsRes, partnersRes, sponsorsRes, settingsRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/socials`),
                    axios.get(`${API_BASE_URL}/currentDivision`),
                    axios.get(`${API_BASE_URL}/partners`),
                    axios.get(`${API_BASE_URL}/sponsors`),
                    axios.get(`${API_BASE_URL}/settings`)
                ]);

                if (!isMounted) return;
                
                if (socialsRes.data.socials) setSocials(socialsRes.data.socials);
                setDivisions(divisionsRes.data || []);
                setPartners(partnersRes.data.partners || []);
                setSponsor(sponsorsRes.data.sponsors || null);
                if (settingsRes.data.settings) setSettings(settingsRes.data.settings);
            } catch (err) {
                console.error("Footer data fetch error:", err);
            }
        };

        fetchData();
        return () => { isMounted = false; };
    }, []);

    const handleScrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    const styles = {
        footer: {
            background: '#040960',
            width: '100%',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
        },
        pattern: {
            position: 'absolute',
            inset: 0,
            background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 10px)',
            pointerEvents: 'none',
        },
        container: {
            maxWidth: '1280px',
            margin: '0 auto',
            padding: isMobile ? '60px 24px 40px' : '80px 24px 60px',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: isMobile ? '40px' : '60px',
            position: 'relative',
            zIndex: 1,
            textAlign: isMobile ? 'center' : 'left'
        },
        col: { 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '28px',
            alignItems: isMobile ? 'center' : 'flex-start'
        },
        header: {
            fontFamily: "'Anton', sans-serif",
            fontSize: '20px',
            fontWeight: 400,
            color: '#E7C424',
            textTransform: 'uppercase',
            margin: 0,
            letterSpacing: '1.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        mission: {
            fontFamily: 'var(--fontInter)',
            fontSize: '15px',
            lineHeight: '1.7',
            color: 'rgba(255,255,255,0.7)',
            margin: 0
        },
        linkList: { 
            listStyle: 'none', 
            padding: 0, 
            margin: 0, 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '14px',
            alignItems: isMobile ? 'center' : 'flex-start'
        },
        link: {
            fontFamily: 'var(--fontInter)',
            fontSize: '15px',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.8)',
            textDecoration: 'none',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        contactBox: { 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '20px',
            alignItems: isMobile ? 'center' : 'flex-start'
        },
        contactItem: {
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'center' : 'flex-start',
            gap: '14px',
            fontFamily: 'var(--fontInter)',
            fontSize: '14px',
            textAlign: isMobile ? 'center' : 'left'
        },
        iconCircle: {
            width: '36px',
            height: '36px',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#E7C424',
            flexShrink: 0
        },
        socialRow: { display: 'flex', gap: '12px', marginTop: '10px' },
        partnerRibbon: {
            borderTop: '1px solid rgba(255,255,255,0.1)',
            padding: '40px 24px',
            maxWidth: '1280px',
            margin: '0 auto',
            textAlign: 'center'
        },
        bottomBar: {
            background: 'rgba(0,0,0,0.2)',
            padding: '24px 0',
            borderTop: '1px solid rgba(255,255,255,0.05)',
            textAlign: isMobile ? 'center' : 'left'
        }
    };

    return (
        <footer style={styles.footer}>
            <div style={styles.pattern} />
            
            <div style={styles.container}>
                {/* Column 1: Brand & Social */}
                <div style={styles.col}>
                    <Link to="/" onClick={handleScrollToTop}>
                        <img src={logo} alt="BTCL Logo" style={{ width: '130px', height: 'auto', marginBottom: '8px' }} />
                    </Link>
                    <p style={styles.mission}>
                        Conducting elite cricket league matches since 2006. Dedicated to the excellence and future growth of British Tamils cricket.
                    </p>
                    <div style={styles.socialRow}>
                        <SocialIcon href={socials.facebook} icon={<Facebook size={18}/>} />
                        <SocialIcon href={socials.instagram} icon={<Instagram size={18}/>} />
                        <SocialIcon href={socials.twitter} icon={<Twitter size={18}/>} />
                        <SocialIcon href={socials.youtube} icon={<Youtube size={18}/>} />
                    </div>
                </div>

                {/* Column 2: Divisions */}
                <div style={styles.col}>
                    <h3 style={styles.header}>{!isMobile && <Trophy size={20} />} Divisions</h3>
                    <ul style={styles.linkList}>
                        {divisions.map((div) => (
                            <li key={div.PCID}>
                                <Link to={`/leagues/${div.PCID}`} onClick={handleScrollToTop} className="footer-link" style={styles.link}>
                                    {!isMobile && <Shield size={14} opacity={0.5} />} {div.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Column 3: Governance */}
                <div style={styles.col}>
                    <h3 style={styles.header}>{!isMobile && <Shield size={20} />} Resources</h3>
                    <ul style={styles.linkList}>
                        <li><Link to="/about-btcl" onClick={handleScrollToTop} style={styles.link}>About BTCL</Link></li>
                        <li><Link to="/constitution" onClick={handleScrollToTop} style={styles.link}>Constitution</Link></li>
                        <li><Link to="/playing-condition" onClick={handleScrollToTop} style={styles.link}>Playing Condition</Link></li>
                        <li><Link to="/discipline" onClick={handleScrollToTop} style={styles.link}>Discipline</Link></li>
                        <li><Link to="/committee" onClick={handleScrollToTop} style={styles.link}>Committee</Link></li>
                        <li><Link to="/accounting" onClick={handleScrollToTop} style={styles.link}>Accounting</Link></li>
                    </ul>
                </div>

                {/* Column 4: Headquarters */}
                <div style={styles.col}>
                    <h3 style={styles.header}>{!isMobile && <MapPin size={20} />} Connectivity</h3>
                    <div style={styles.contactBox}>
                        <div style={styles.contactItem}>
                            <div style={styles.iconCircle}><Mail size={16}/></div>
                            <div>
                                <span style={{display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 700}}>ELECTRONIC MAIL</span>
                                <a href={`mailto:${settings.admin_email || 'hello@btcluk.com'}`} style={{ color: '#fff', textDecoration: 'none', fontWeight: 600 }}>{settings.admin_email || 'hello@btcluk.com'}</a>
                            </div>
                        </div>
                        <div style={styles.contactItem}>
                            <div style={styles.iconCircle}><MapPin size={16}/></div>
                            <div>
                                <span style={{display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 700}}>HEADQUARTERS</span>
                                <span style={{ lineHeight: '1.5', fontWeight: 600 }}>{settings.address || 'Chessington, Surrey KT9 1AW'}</span>
                            </div>
                        </div>
                        {sponsor && (
                            <div style={styles.contactItem}>
                                <div style={styles.iconCircle}><Trophy size={16}/></div>
                                <div>
                                    <span style={{display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 700}}>PROUD SPONSOR</span>
                                    <span style={{ fontWeight: 600, color: '#E7C424' }}>{sponsor.name}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Partners Ribbon */}
            {partners.length > 0 && (
                <div style={styles.partnerRibbon}>
                    <span style={{display: 'block', fontFamily: 'var(--fontInter)', fontSize: '11px', fontWeight: 800, color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', marginBottom: '24px'}}>OFFICIAL LEAGUE PARTNERS</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '40px', alignItems: 'center' }}>
                        {partners.map((partner) => (
                            <motion.a 
                                key={partner.id}
                                href={partner.link || '#'} 
                                target="_blank"
                                whileHover={{ scale: 1.05, filter: 'grayscale(0%)' }}
                                style={{ filter: 'grayscale(100%)', opacity: 0.6, transition: 'all 0.3s ease' }}
                            >
                                <img src={`${LOGO_BASE_URL}/${partner.logo}`} alt={partner.name} style={{ height: '40px', width: 'auto' }} />
                            </motion.a>
                        ))}
                    </div>
                </div>
            )}

            {/* Bottom Bar */}
            <div style={styles.bottomBar}>
                <div style={{ 
                    maxWidth: '1280px', 
                    margin: '0 auto', 
                    padding: '0 24px',
                    display: 'flex',
                    justifyContent: isMobile ? 'center' : 'space-between',
                    alignItems: 'center',
                    fontFamily: 'var(--fontInter)',
                    fontSize: '13px',
                    flexWrap: 'wrap',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '16px' : '24px',
                    textAlign: 'center'
                }}>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
                        © {new Date().getFullYear()} BRITISH TAMILS CRICKET LEAGUE. ALL RIGHTS RESERVED.
                    </span>
                    <div style={{ display: 'flex', gap: '32px', justifyContent: 'center' }}>
                        <Link to="/terms" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Terms of Use</Link>
                        <Link to="/privacy" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Privacy Policy</Link>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.3)', fontSize: '11px', fontWeight: 700, justifyContent: 'center' }}>
                        POWERED BY <span style={{ color: 'rgba(255,255,255,0.6)' }}>THAAYAKAM</span>
                    </div>
                </div>
            </div>

            <style>{`
                .footer-link:hover {
                    color: #E7C424 !important;
                    transform: translateX(5px);
                }
            `}</style>
        </footer>
    );
}

const SocialIcon = ({ href, icon }) => (
    <motion.a
        href={href}
        target="_blank"
        rel="noreferrer"
        whileHover={{ y: -3, background: '#E7C424', color: '#060D8A' }}
        style={{
            width: '40px',
            height: '40px',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            transition: 'all 0.3s ease'
        }}
    >
        {icon}
    </motion.a>
);

export default Footer;